import React, { useMemo, useState } from 'react';
import usePersistedState from './utils/usePersistedState';
import { FaAngleDoubleRight, FaCube, FaInfoCircle, FaTable } from 'react-icons/fa';
import './css/colors.css';
import './css/global.css';
import Login from './component/app/Login';
import SideNav from './component/app/SideNav';
import Content from './component/app/Content';
import Configuration from './component/introduction/Configuration';
import DataInputs from './component/introduction/DataInputs';
import OperationFlow from './component/introduction/OperationFlow';
import ResultOutputs from './component/introduction/ResultOutputs';
import EmployeeData from './component/data/EmployeeData';
import SkillData from './component/data/SkillData';
import RegionData from './component/data/RegionData';
import OfficeData from './component/data/OfficeData';
import ServiceCategoryData from './component/data/ServiceCategoryData';
import RunSchedule from './component/operation/RunSchedule';
import STISchedule from './component/operation/STI/STISchedule';
import Redistribution from './component/operation/Redistribution';
import ScheduleResult from './component/result/ScheduleResult';
import STIScheduleResult from './component/result/STIScheduleResult';

const GlobalContext = React.createContext(undefined);

export default function App() {
  // userinfo
  const [loggedin, setLoggedin] = useState(false);
  const [username, setUsername] = usePersistedState('username', '');
  const [userGrade, setUserGrade] = useState(0);

  // Default Path
  const [path, setPath] = useState('/data/employees');
  // Side nav Icon
  const infoIcon = useMemo(() => <FaInfoCircle />, []);
  const dataIcon = useMemo(() => <FaTable />, []);
  const operationIcon = useMemo(() => <FaAngleDoubleRight />, []);
  const resultIcon = useMemo(() => <FaCube />, []);
  // Side nav
  const navMenuItems = useMemo(
    () => [
      {
        icon: infoIcon,
        title: '說明',
        items: [
          {
            path: '/info/configuration',
            title: '配置要求',
            key: 0,
          },
          {
            path: '/info/inputs',
            title: '數據與輸入',
          },
          {
            path: '/info/operationflow',
            title: '操作流程',
          },
          {
            path: '/info/outputs',
            title: '結果與輸出',
          },
        ],
      },
      {
        icon: dataIcon,
        title: '數據',
        isOpen: true,
        items: [
          {
            path: '/data/employees',
            title: '員工數據',
          },
          {
            path: '/data/skills',
            title: '技能數據',
          },
          {
            path: '/data/serviceCategories',
            title: '服務數據',
          },
          {
            path: '/data/regions',
            title: '分區數據',
          },
          {
            path: '/data/offices',
            title: '办公室數據',
          },
        ]
      },
      {
        icon: operationIcon,
        title: '操作',
        isOpen: true,
        items: [
          {
            path: '/operation/runschedule',
            title: '員工排班',
          },
          {
            path: '/operation/stischedule',
            title: 'STI排班',
          },
          {
            path: '/operation/redistribution',
            title: '散單重分配',
          },
        ],
      },
      {
        icon: resultIcon,
        title: '結果',
        isOpen: true,
        items: [
          {
            path: '/result/scheduleresult',
            title: '排班方案',
          },
          {
            path: '/result/stischeduleresult',
            title: 'STI排班方案',
          },
          {
            path: '/result/redistributionresult',
            title: '散單重分配方案',
          },
        ],
      },
    ],
    [username]
  );
  // Pages
  const pages = useMemo(() => {
    const p = [
      {
        path: '/info/configuration',
        component: () => <Configuration />,
      },
      {
        path: '/info/inputs',
        component: () => <DataInputs />,
      },
      {
        path: '/info/operationflow',
        component: () => <OperationFlow />,
      },
      {
        path: '/info/outputs',
        component: () => <ResultOutputs />,
      },
      {
        path: '/data/employees',
        component: () => <EmployeeData />,
      },
      {
        path: '/data/skills',
        component: () => <SkillData />,
      },
      {
        path: '/data/serviceCategories',
        component: () => <ServiceCategoryData />,
      },
      {
        path: '/data/regions',
        component: () => <RegionData />,
      },
      {
        path: '/data/offices',
        component: () => <OfficeData />,
      },
      {
        path: '/operation/runschedule',
        component: () => <RunSchedule />,
      },
      {
        path: '/operation/stischedule',
        component: () => <STISchedule />,
      },
      {
        path: '/operation/redistribution',
        component: () => <Redistribution />,
      },
      {
        path: '/result/scheduleresult',
        component: () => <ScheduleResult />,
      },
      {
        path: '/result/stischeduleresult',
        component: () => <STIScheduleResult />,
      },
      {
        path: '/result/redistributionresult',
        component: () => <Redistribution />,
      },
    ];
    return p;
  }, [username]);

  // 员工数据
  const [employeeDatas, setEmployeeDatas] = usePersistedState('employee', []);
  // 技能数据
  const [skillDatas, setSkillDatas] = usePersistedState('skill', []);
  // 服务数据
  const [serviceDatas, setServiceDatas] = usePersistedState('service', []);
  // 分區数据
  const [regionDatas, setRegionDatas] = usePersistedState('region', []);
  // 办公室数据
  const defaultOffice = [
    {
      "key": 0,
      "officeName": "衛星辦公室（屯門）",
      "serviceableArea": "新界、九龍",
      "priority": "1、2"
    }
  ];
  const [officeDatas, setOfficeDatas] = usePersistedState('office', defaultOffice);
  // 已排班数据
  const [scheduledData, setScheduledData] = usePersistedState('scheduled', []);
  // 本月每日工作单
  const [dailyData, setDailyData] = usePersistedState('daily', []);
  // 旧单重排
  const [prevOrderData, setPrevOrderData] = usePersistedState('prevOrder', []);
  // 新订单
  const [newOrderData, setNewOrderData] = usePersistedState('newOrder', []);
  // 员工信息
  const [staffDataSource, setStaffDataSource] = usePersistedState('staff', []);
  // STI全部工单
  // const [STIData, setSTIData] = usePersistedState('sti', []);
  const [STIData, setSTIData] = useState([]);
  // T701_STI
  // const [STIOrderData, setSTIOrderData] = usePersistedState('stiOrder', []);
  const [STIOrderData, setSTIOrderData] = useState([]);
  // T702_焗霧
  // const [fogOrderData, setFogOrderData] = usePersistedState('fogOrder', []);
  const [fogOrderData, setFogOrderData] = useState([]);

  // 排班类型
  const [runType, setRunType] = useState('normal');

  // 散單重分配數據
  const [scheduleData, setScheduleData] = useState([]);
  const [scatteredData, setScatteredData] = useState([]);
  const [redistributionResult, setRedistributionResult] = useState(null);

  // result data
  const [staffResult, setStaffResult] = useState([]);
  const [perStaffScheduledResult, setPerStaffScheduledResult] = useState([]);
  const [allStaffScheduledResult, setAllStaffScheduledResult] = useState([]);
  const [prevOrderResult, setPrevOrderResult] = useState([]);
  const [newOrderResult, setNewOrderResult] = useState([]);
  const [otherOrderResult, setOtherOrderResult] = useState([]);
  const [STIResult, setSTIResult] = useState([]);
  const [result701, setResult701] = useState([]);
  const [result702, setResult702] = useState([]);

  return (
    <GlobalContext.Provider
      value={{
        path,
        setPath,
        username,
        userGrade,
        setUsername,
        setUserGrade,
        employeeDatas,
        setEmployeeDatas,
        skillDatas,
        setSkillDatas,
        serviceDatas,
        setServiceDatas,
        regionDatas,
        setRegionDatas,
        officeDatas, 
        setOfficeDatas,
        scheduledData,
        setScheduledData,
        dailyData,
        setDailyData,
        prevOrderData,
        setPrevOrderData,
        newOrderData,
        setNewOrderData,
        staffDataSource,
        setStaffDataSource,
        staffResult,
        setStaffResult,
        perStaffScheduledResult,
        setPerStaffScheduledResult,
        allStaffScheduledResult,
        setAllStaffScheduledResult,
        prevOrderResult,
        setPrevOrderResult,
        newOrderResult,
        setNewOrderResult,
        otherOrderResult,
        setOtherOrderResult,
        STIData,
        setSTIData,
        STIOrderData,
        setSTIOrderData,
        fogOrderData,
        setFogOrderData,
        STIResult, 
        setSTIResult,
        result701, 
        setResult701,
        result702, 
        setResult702,
        runType,
        setRunType,
        scheduleData,
        setScheduleData,
        scatteredData,
        setScatteredData,
        redistributionResult,
        setRedistributionResult,
      }}
    >
      <div
        className="app"
        style={{ height: '100%', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {/* <TitleBar /> */}
        {!loggedin ?
          (<Login setLoggedin={setLoggedin} />) :
          (
            <div
              className="main"
              style={{
                height: 'calc(100% - 30px)',
                display: 'flex',
              }}
            >
              <SideNav menuItems={navMenuItems} path={path} setPath={setPath} />
              <Content pages={pages} path={path} />
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  padding: 5,
                }}
              >
                工號: {username}&nbsp;&nbsp;&nbsp;權限級別: {userGrade}
              </div>
            </div>
          )}
      </div>
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  const context = React.useContext(GlobalContext);
  if (context === undefined) throw new Error('useGlobalContext must be used within a GlobalContext.Provider');
  return context;
}
